import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import {
    getPageLayoutOptions,
    getPageLayoutPreset,
    hasPageCover,
    type PageLayoutOptions,
    type RevisionPageDocument,
} from '@gitbook/hive-core';
import {
    Description,
    Label,
    LabeledContainer,
    LabeledContainerBody,
    LabeledContainerHeader,
    Separator,
} from '@gitbook/spine-react';

import { PageOptionsContent } from './PageOptionsContent';
import { PageOptionsCoverImage } from './PageOptionsCoverImage';
import { PageOptionsFooter } from './PageOptionsFooter';
import { PageOptionsLayout } from './PageOptionsLayout';
import { PageOptionsSidebars } from './PageOptionsSidebars';
import { PageOptionsWidth } from './PageOptionsWidth';
import type { PageLayoutOptionsProps, PageLayoutOptionsState, PageOptionsTabProps } from './types';

/**
 * Tab for the layout options.
 */
export function PageOptionsTabLayout(props: PageOptionsTabProps): React.ReactElement {
    const { space, branch, analyticsContext, page } = props;

    const pageOptions = React.useMemo<PageLayoutOptionsState>(
        () => ({
            pageKey: page.id,
            layoutPreset: getPageLayoutPreset(page),
            layoutOptions: getPageLayoutOptions(page),
        }),
        [page]
    );

    const pageOptionsProps = React.useMemo<PageLayoutOptionsProps>(() => {
        return {
            pageOptions,
            setPageOptionsUpdate: (update) => {
                let analyticsPageProperties: string[] = [];

                branch.change((change) => {
                    if (update.layoutPreset) {
                        // If we're changing the layout preset, we need to reset the layout options
                        change.updatePage(page.id, {
                            layout:
                                update.layoutPreset === 'custom'
                                    ? pageOptions.layoutOptions
                                    : update.layoutPreset,
                        });
                        analyticsPageProperties.push('pageLayout');
                    } else if (update.layoutOptions) {
                        change.updatePage(page.id, {
                            layout: update.layoutOptions,
                        });
                        analyticsPageProperties = mapPageOptionsUpdateToPageProperties(
                            page,
                            update.layoutOptions
                        );
                    }
                });

                analytics.logEvent(
                    analytics.createPageEvent(space, page, {
                        ...analyticsContext,
                        type: 'page_edit_options',
                        pageProperties: analyticsPageProperties,
                    })
                );
            },
        };
    }, [page, pageOptions, branch, space, analyticsContext]);

    return (
        <>
            <LabeledContainer>
                <LabeledContainerHeader>
                    <Label>Layout preset</Label>
                    <Description>
                        Use presets to quickly set all page options. You can also set each option
                        individually below.
                    </Description>
                </LabeledContainerHeader>
                <LabeledContainerBody>
                    <PageOptionsLayout {...pageOptionsProps} />
                </LabeledContainerBody>
            </LabeledContainer>

            <Separator />

            <LabeledContainer>
                <LabeledContainerHeader>
                    <Label>Page width</Label>
                    <Description>
                        You can always set individual blocks to be full width in the block's
                        options.
                    </Description>
                </LabeledContainerHeader>
                <LabeledContainerBody>
                    <PageOptionsWidth {...pageOptionsProps} />
                </LabeledContainerBody>
            </LabeledContainer>

            <Separator />

            {hasPageCover(page) ? (
                // Only show the cover image options if the page has a cover image set
                <>
                    <LabeledContainer>
                        <LabeledContainerHeader>
                            <Label>Images</Label>
                        </LabeledContainerHeader>
                        <LabeledContainerBody>
                            <PageOptionsCoverImage {...pageOptionsProps} />
                        </LabeledContainerBody>
                    </LabeledContainer>
                    <Separator />
                </>
            ) : null}

            <LabeledContainer>
                <LabeledContainerHeader>
                    <Label>Content</Label>
                </LabeledContainerHeader>
                <LabeledContainerBody>
                    <PageOptionsContent {...pageOptionsProps} />
                </LabeledContainerBody>
            </LabeledContainer>

            <Separator />

            <LabeledContainer>
                <LabeledContainerHeader>
                    <Label>Sidebars</Label>
                </LabeledContainerHeader>
                <LabeledContainerBody>
                    <PageOptionsSidebars {...pageOptionsProps} />
                </LabeledContainerBody>
            </LabeledContainer>

            <Separator />

            <LabeledContainer>
                <LabeledContainerHeader>
                    <Label>Footer</Label>
                </LabeledContainerHeader>
                <LabeledContainerBody>
                    <PageOptionsFooter {...pageOptionsProps} />
                </LabeledContainerBody>
            </LabeledContainer>
        </>
    );
}

/**
 * This function returns a list of page properties that are changing
 * from the current page. It will be used to record analytics events
 * for page options changes.
 */
function mapPageOptionsUpdateToPageProperties(
    page: RevisionPageDocument,
    update: PageLayoutOptions
): Array<Partial<keyof analytics.AnalyticsPageIdentity>> {
    const pageProperties: Array<Partial<keyof analytics.AnalyticsPageIdentity>> = [];

    const updateKeys = Object.keys(update) as Array<keyof PageLayoutOptions>;
    const layoutOptions = getPageLayoutOptions(page);
    for (const key of updateKeys) {
        // If the layout option is changing, we record that property change
        if (layoutOptions[key] !== update[key]) {
            switch (key) {
                case 'cover':
                    pageProperties.push('pageHasCoverVisible');
                    break;
                case 'coverSize':
                    pageProperties.push('pageCoverSize');
                    break;
                case 'width':
                    pageProperties.push('pageWidth');
                    break;
                case 'title':
                    pageProperties.push('pageHasTitleVisible');
                    break;
                case 'description':
                    pageProperties.push('pageHasDescriptionVisible');
                    break;
                case 'tableOfContents':
                    pageProperties.push('pageHasTableOfContentsVisible');
                    break;
                case 'outline':
                    pageProperties.push('pageHasOutlineVisible');
                    break;
                case 'pagination':
                    pageProperties.push('pageHasPaginationVisible');
                    break;
                case 'metadata':
                    pageProperties.push('pageHasMetadataVisible');
                    break;
            }
        }
    }

    return pageProperties;
}
